import { useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Grid2,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useProjects } from "../../hooks/useProjects";
import Header from "../../components/Header";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AnimatedButton } from "../../ui/AnimatedButton";
import { AutoAwesome, CircleOutlined, Circle } from "@mui/icons-material";
import React from "react";
import Footer from "../home/Footer";
import StyledTypography from "../../ui/StyledTypography";

const ProjectDetails = () => {
  const theme = useTheme();
  const { slug } = useParams();
  const { project, loading, error } = useProjects(slug);

  const [activeSection, setActiveSection] = useState<string | null>(null);

  useEffect(() => {
    const onScroll = () => {
      const sections =
        project?.table_of_contents?.map((content) =>
          document.getElementById(content.toLowerCase().replace(/\s+/g, "-"))
        ) || [];

      let currentSection: string | null = null;

      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];

        if (section) {
          const rect = section.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            currentSection = section.id;
            break;
          }
        }
      }

      setActiveSection(currentSection);
    };

    window.addEventListener("scroll", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [project]);

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ px: 2, py: { xs: 4, md: 8 } }}>
          <Header />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography variant="h1" fontWeight={"bold"} color="red">
              {error}
            </Typography>
          </motion.div>
        </Box>
      </Container>
    );
  }
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ px: 2, py: { xs: 4, md: 8 } }}>
          <Header />
        </Box>
        <Stack direction={"row"} spacing={4} alignItems={"center"} mb={5}>
          <Skeleton
            animation="wave"
            variant="circular"
            width={150}
            height={150}
          />
          <div>
            <Skeleton animation="wave" variant="text" width={200} height={50} />
            <Skeleton animation="wave" variant="text" width={150} height={30} />
          </div>
        </Stack>

        <Skeleton
          animation="wave"
          variant="rectangular"
          width="100%"
          height={500}
          sx={{ mb: 5, borderRadius: 20 }}
        />

        <Grid2 container spacing={4} mb={4}>
          <Grid2
            size={{ xs: 12, md: 8 }}
            sx={{ display: "flex", flexDirection: "column", gap: 4 }}
          >
            <Grid2 size={12}>
              <Skeleton
                animation="wave"
                variant="text"
                width={200}
                height={40}
              />
              <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height={200}
                sx={{ mt: 2, borderRadius: 5 }}
              />
            </Grid2>

            <Grid2 size={12}>
              <Skeleton
                animation="wave"
                variant="text"
                width={200}
                height={40}
              />
              <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height={200}
                sx={{ mt: 2, borderRadius: 5 }}
              />
            </Grid2>

            <Grid2 size={12}>
              <Skeleton
                animation="wave"
                variant="text"
                width={200}
                height={40}
              />
              <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height={200}
                sx={{ mt: 2, borderRadius: 5 }}
              />
            </Grid2>

            <Grid2 size={12}>
              <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height={200}
                sx={{ mt: 2, borderRadius: 5 }}
              />
            </Grid2>
          </Grid2>

          <Grid2
            size={{ xs: 12, md: 4 }}
            sx={{ display: "flex", flexDirection: "column", gap: 4 }}
          >
            <Grid2 size={12}>
              <Skeleton
                animation="wave"
                variant="text"
                width={200}
                height={40}
              />
              <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height={200}
                sx={{ mt: 2, borderRadius: 5 }}
              />
            </Grid2>

            <Grid2 size={12}>
              <Skeleton
                animation="wave"
                variant="text"
                width={200}
                height={40}
              />
              <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height={200}
                sx={{ mt: 2, borderRadius: 5 }}
              />
            </Grid2>

            <Grid2 size={12}>
              <Skeleton
                animation="wave"
                variant="text"
                width="80%"
                height={40}
              />
              <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height={200}
                sx={{ mt: 2, borderRadius: 5 }}
              />
            </Grid2>

            <Grid2 size={12}>
              <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height={200}
                sx={{ mt: 2, borderRadius: 5 }}
              />
            </Grid2>
          </Grid2>
        </Grid2>

        <Stack spacing={4}>
          <Skeleton
            animation="wave"
            variant="rectangular"
            width="100%"
            height={500}
            sx={{ mb: 5, borderRadius: 20 }}
          />

          <Box>
            <Skeleton animation="wave" variant="text" width={200} height={50} />
            <React.Fragment>
              <Skeleton animation="wave" height={30} width={"100%"} />
              <Skeleton animation="wave" height={30} width={"100%"} />
              <Skeleton animation="wave" height={30} width={"100%"} />
              <Skeleton animation="wave" height={30} width="60%" />
            </React.Fragment>
          </Box>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
      <Box sx={{ px: 2, py: { xs: 4, md: 8 } }}>
        <Header />
      </Box>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Stack direction={"row"} spacing={4} alignItems={"center"} mb={5}>
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Box
              component="img"
              alt={`${project?.name}-logo`}
              src={project?.logo}
              sx={{ height: 150, width: 150 }}
            />
          </motion.div>
          <div>
            <StyledTypography
              variant="h2"
              fontWeight={"bold"}
              colors={[theme.palette.grey[300], theme.palette.grey[500]]}
            >
              {project?.name}
            </StyledTypography>
            <StyledTypography
              variant="h4"
              colors={[theme.palette.grey[500], theme.palette.grey[900]]}
            >
              {project?.category}
            </StyledTypography>
          </div>
        </Stack>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <Box
          component="img"
          alt={`${project?.name}-logo`}
          src={project?.image[1]}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            imageRendering: "auto",
            display: "block",
            mb: 5,
          }}
        />
      </motion.div>

      <Grid2 container spacing={4} mb={4}>
        <Grid2
          size={{ xs: 12, md: 8 }}
          sx={{ display: "flex", flexDirection: "column", gap: 4 }}
        >
          <AnimatePresence>
            {project?.overview && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <Grid2 size={12}>
                  <Card id="overview">
                    <CardContent>
                      <StyledTypography
                        variant="h3"
                        fontWeight={"bold"}
                        colors={[theme.palette.grey[300], theme.palette.grey[500]]}
                        gutterBottom
                      >
                        Overview
                      </StyledTypography>
                      <Divider
                        sx={{ marginY: 2, borderColor: "rgba(255, 255, 255, .1)" }}
                      />
                      <StyledTypography
                        variant="body1"
                        colors={[theme.palette.grey[400], theme.palette.grey[700]]}
                        dangerouslySetInnerHTML={{ __html: project?.overview ?? "" }}
                      />
                    </CardContent>
                  </Card>
                </Grid2>
              </motion.div>
            )}

            {project?.process && (
              <motion.div
                key="process"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Grid2 size={12}>
                  <Card id="process">
                    <CardContent>
                      <StyledTypography
                        variant="h3"
                        fontWeight={"bold"}
                        colors={[theme.palette.grey[300], theme.palette.grey[500]]}
                        gutterBottom
                      >
                        Process
                      </StyledTypography>
                      <Divider
                        sx={{ marginY: 2, borderColor: "rgba(255, 255, 255, .1)" }}
                      />
                      <StyledTypography
                        variant="body1"
                        colors={[theme.palette.grey[400], theme.palette.grey[700]]}
                        dangerouslySetInnerHTML={{ __html: project?.process ?? "" }}
                      />
                    </CardContent>
                  </Card>
                </Grid2>
              </motion.div>
            )}

            {project?.development && (
              <motion.div
                key="development"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Grid2 size={12}>
                  <Card id="development">
                    <CardContent>
                      <StyledTypography
                        variant="h3"
                        fontWeight={"bold"}
                        colors={[theme.palette.grey[300], theme.palette.grey[500]]}
                        gutterBottom
                      >
                        Development
                      </StyledTypography>
                      <Divider
                        sx={{ marginY: 2, borderColor: "rgba(255, 255, 255, .1)" }}
                      />
                      <StyledTypography
                        variant="body1"
                        colors={[theme.palette.grey[400], theme.palette.grey[700]]}
                        dangerouslySetInnerHTML={{
                          __html: project?.development ?? "",
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid2>
              </motion.div>
            )}

            {project?.image[2] && (
              <motion.div
                key="image2"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Grid2 size={12}>
                  <Box
                    component="img"
                    src={project?.image[2]}
                    sx={{
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </Grid2>
              </motion.div>
            )}

            {project?.image[3] && (
              <motion.div
                key="image3"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <Grid2 size={12}>
                  <Box
                    component="img"
                    src={project?.image[3]}
                    sx={{
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </Grid2>
              </motion.div>
            )}
          </AnimatePresence>
        </Grid2>

        <Grid2
          size={{ xs: 12, md: 4 }}
          sx={{ display: "flex", flexDirection: "column", gap: 4 }}
        >
          <AnimatePresence>
            {project?.tech && (
              <motion.div
                key="tech"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
              >
                <Grid2 size={12}>
                  <Card aria-label="Tech">
                    <CardContent>
                      <StyledTypography
                        variant="h3"
                        fontWeight="bold"
                        colors={[theme.palette.grey[300], theme.palette.grey[500]]}
                        gutterBottom
                      >
                        Tech
                      </StyledTypography>
                      <Divider
                        sx={{ marginY: 2, borderColor: "rgba(255, 255, 255, .1)" }}
                      />
                      <List>
                        {project?.tech?.map((tech, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                          >
                            <ListItem>
                              <Box
                                component={"img"}
                                src={tech.icon}
                                alt={`Tech icon ${index + 1}`}
                                sx={{ height: 60, width: 60, mr: 2 }}
                              />
                              <ListItemText
                                primary={tech.title}
                                primaryTypographyProps={{
                                  letterSpacing: "-0.02em",
                                  variant: "body1",
                                  fontWeight: 400,
                                }}
                              />
                            </ListItem>
                          </motion.div>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid2>
              </motion.div>
            )}

            {project?.table_of_contents && (
              <motion.div
                key="contents"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Grid2 size={12}>
                  <Card aria-label="Table of Contents">
                    <CardContent>
                      <StyledTypography
                        variant="h3"
                        fontWeight="bold"
                        colors={[theme.palette.grey[300], theme.palette.grey[500]]}
                        gutterBottom
                      >
                        Contents
                      </StyledTypography>
                      <Divider
                        sx={{ marginY: 2, borderColor: "rgba(255, 255, 255, .1)" }}
                      />
                      <List>
                        {project?.table_of_contents?.map((content, index) => {
                          const anchor = content.toLowerCase().replace(/\s+/g, "-");
                          const isActive = activeSection === anchor;

                          return (
                            <ListItem key={index} disablePadding>
                              <motion.div
                                initial={{ opacity: 0.5 }}
                                animate={{
                                  opacity: isActive ? 1 : 0.7,
                                  scale: isActive ? 1.05 : 1,
                                }}
                                transition={{ duration: 0.3 }}
                              >
                                <ListItemButton
                                  href={`#${anchor}`}
                                  sx={{
                                    color: isActive
                                      ? theme.palette.common.white
                                      : "inherit",
                                    fontWeight: isActive ? 700 : 300,
                                    transition: "color .3s ease",
                                    "&:hover": {
                                      color: theme.palette.common.white,
                                    },
                                  }}
                                >
                                  <Circle
                                    sx={{
                                      fontSize: 12,
                                      color: isActive
                                        ? theme.palette.text.secondary
                                        : theme.palette.grey[500],
                                      mr: 1,
                                    }}
                                  />
                                  <ListItemText
                                    primary={content}
                                    primaryTypographyProps={{
                                      letterSpacing: "-0.02em",
                                      variant: "body1",
                                      fontWeight: 400,
                                    }}
                                  />
                                </ListItemButton>
                              </motion.div>
                            </ListItem>
                          );
                        })}
                      </List>
                    </CardContent>
                  </Card>
                </Grid2>
              </motion.div>
            )}

            {project?.features && (
              <motion.div
                key="features"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Grid2 size={12}>
                  <Card aria-label="features" sx={{ position: "relative" }}>
                    <CardContent>
                      <StyledTypography
                        variant="h3"
                        fontWeight="bold"
                        colors={[theme.palette.grey[300], theme.palette.grey[500]]}
                        gutterBottom
                      >
                        Features
                        <AutoAwesome
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: -100,
                            color: "white",
                            fontSize: 300,
                            transform: "rotate(135deg)",
                            opacity: 0.05,
                            zIndex: -1,
                          }}
                        />
                      </StyledTypography>
                      <Divider
                        sx={{ marginY: 2, borderColor: "rgba(255, 255, 255, .1)" }}
                      />
                      <List>
                        {project?.features?.map((features, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                          >
                            <ListItem disablePadding>
                              <CircleOutlined sx={{ fontSize: 12, mr: 1 }} />
                              <ListItemText
                                primary={features}
                                primaryTypographyProps={{
                                  letterSpacing: "-0.02em",
                                  variant: "body1",
                                  fontWeight: 400,
                                }}
                              />
                            </ListItem>
                          </motion.div>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid2>
              </motion.div>
            )}

            {project?.pages && (
              <motion.div
                key="pages"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Grid2 size={12}>
                  <Card aria-label="pages">
                    <CardContent>
                      <StyledTypography
                        variant="h3"
                        fontWeight="bold"
                        colors={[theme.palette.grey[300], theme.palette.grey[500]]}
                        gutterBottom
                      >
                        Pages
                      </StyledTypography>
                      <Divider
                        sx={{ marginY: 2, borderColor: "rgba(255, 255, 255, .1)" }}
                      />
                      <List>
                        {project?.pages?.map((page, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                          >
                            <ListItem disablePadding>
                              <ListItemText
                                primary={`${index + 1}. ${page}`}
                                primaryTypographyProps={{
                                  letterSpacing: "-0.02em",
                                  variant: "body1",
                                  fontWeight: 400,
                                }}
                              />
                            </ListItem>
                          </motion.div>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid2>
              </motion.div>
            )}

            {project?.created && (
              <motion.div
                key="created"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <Grid2 size={12}>
                  <Card aria-label="Created">
                    <CardContent>
                      <StyledTypography
                        variant="h3"
                        fontWeight="bold"
                        colors={[theme.palette.grey[300], theme.palette.grey[500]]}
                        gutterBottom
                      >
                        Created
                      </StyledTypography>
                      <Divider
                        sx={{ marginY: 2, borderColor: "rgba(255, 255, 255, .1)" }}
                      />
                      <StyledTypography
                        variant="h4"
                        fontWeight="bold"
                        colors={[theme.palette.grey[300], theme.palette.grey[500]]}
                      >
                        {project?.created}
                      </StyledTypography>
                    </CardContent>
                  </Card>
                </Grid2>
              </motion.div>
            )}

            {project?.demo && (
              <motion.div
                key="demo"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <Grid2 size={12}>
                  <AnimatedButton
                    aria-label="demo"
                    component={"a"}
                    href={project?.demo}
                    sx={{
                      height: "4rem",
                      width: "100%",
                      textShadow: "none",
                    }}
                  >
                    <StyledTypography
                      variant="h3"
                      fontWeight={"bold"}
                      colors={[theme.palette.grey[200], theme.palette.grey[700]]}
                    >
                      Live Demo
                    </StyledTypography>
                  </AnimatedButton>
                </Grid2>
              </motion.div>
            )}

            {project?.source_code && (
              <motion.div
                key="source-code"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <Grid2 size={12}>
                  <AnimatedButton
                    aria-label="source-code"
                    component={"a"}
                    href={project?.source_code}
                    sx={{
                      height: "4rem",
                      width: "100%",
                      textShadow: "none",
                    }}
                  >
                    <StyledTypography
                      variant="h3"
                      fontWeight={"bold"}
                      colors={[theme.palette.grey[200], theme.palette.grey[700]]}
                    >
                      Source Code
                    </StyledTypography>
                  </AnimatedButton>
                </Grid2>
              </motion.div>
            )}

            {project?.image[4] && (
              <motion.div
                key="image4"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <Grid2 size={12}>
                  <Box
                    component="img"
                    src={project?.image[4]}
                    sx={{
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </Grid2>
              </motion.div>
            )}
          </AnimatePresence>
        </Grid2>
      </Grid2>

      {/*   Bottom Content   */}
      <Stack spacing={4} mb={4}>
        {project?.image[5] && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <Box
              component="img"
              src={project?.image[5]}
              sx={{
                width: "100%",
                height: "100%",
              }}
            />
          </motion.div>
        )}

        {project?.conclusion && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
          >
            <Box id="conclusion">
              <StyledTypography
                variant="h3"
                fontWeight={"bold"}
                colors={[theme.palette.grey[300], theme.palette.grey[500]]}
                gutterBottom
              >
                Conclusion
              </StyledTypography>
              <Divider
                sx={{ marginY: 2, borderColor: "rgba(255, 255, 255, .1)" }}
              />
              <StyledTypography
                variant="body1"
                colors={[theme.palette.grey[400], theme.palette.grey[700]]}
                dangerouslySetInnerHTML={{ __html: project?.conclusion ?? "" }}
              />
            </Box>
          </motion.div>
        )}
      </Stack>

      <Footer />
    </Container>
  );
};

export default ProjectDetails;